import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fa', label: 'Persian' },
  { code: 'ar', label: 'Arabic' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'tr', label: 'Turkish' },
  { code: 'hi', label: 'Hindi' },
];

interface QuoteFormData {
  text: string;
  authorId: number | null;
  categoryId: number | null;
  language: string;
  isFeatured: boolean;
  isPublished: boolean;
  scheduledAt: string;
  tags: string[];
}

export function QuoteEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [form, setForm] = useState<QuoteFormData>({
    text: '',
    authorId: null,
    categoryId: null,
    language: 'en',
    isFeatured: false,
    isPublished: false,
    scheduledAt: '',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: authors } = useQuery({
    queryKey: ['authors'],
    queryFn: async () => {
      const res = await api.authors.getAll();
      return res.data;
    },
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await api.categories.getAll();
      return res.data;
    },
  });

  const { data: existingQuote, isLoading: loadingQuote } = useQuery({
    queryKey: ['admin-quote', id],
    queryFn: async () => {
      if (!id) return null;
      const res: any = await api.admin.getQuotes();
      const quotes = res.data ?? res;
      return Array.isArray(quotes) ? quotes.find((q: any) => q.id === Number(id)) : null;
    },
    enabled: isEditing,
  });

  useEffect(() => {
    if (existingQuote) {
      setForm({
        text: existingQuote.text || '',
        authorId: existingQuote.authorId ?? existingQuote.author_id ?? null,
        categoryId: existingQuote.categoryId ?? existingQuote.category_id ?? null,
        language: existingQuote.language || 'en',
        isFeatured: existingQuote.isFeatured ?? existingQuote.is_featured ?? false,
        isPublished: existingQuote.isPublished ?? existingQuote.is_published ?? false,
        scheduledAt: existingQuote.scheduledAt ?? existingQuote.scheduled_at ?? '',
        tags: existingQuote.tags ?? [],
      });
    }
  }, [existingQuote]);

  const saveMutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditing && id) {
        return api.admin.updateQuote(Number(id), data);
      }
      return api.admin.createQuote(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quotes'] });
      navigate('/admin/quotes');
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.text.trim()) newErrors.text = 'Quote text is required';
    if (!form.authorId) newErrors.authorId = 'Author is required';
    if (!form.categoryId) newErrors.categoryId = 'Category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    saveMutation.mutate(form);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const updateField = <K extends keyof QuoteFormData>(key: K, value: QuoteFormData[K]) => {
    setForm({ ...form, [key]: value });
    if (errors[key]) setErrors({ ...errors, [key]: '' });
  };

  if (isEditing && loadingQuote) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/quotes')}
          className="p-2 rounded-xl hover:bg-surface-100 text-surface-600 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-surface-900">
            {isEditing ? 'Edit Quote' : 'New Quote'}
          </h1>
          <p className="text-surface-500 mt-1">
            {isEditing ? 'Update the quote details' : 'Create a new quote for your platform'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card variant="glass" className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">
              Quote Text <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.text}
              onChange={(e) => updateField('text', e.target.value)}
              rows={4}
              className={`w-full rounded-2xl border bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 placeholder:text-surface-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none ${
                errors.text ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-surface-200'
              }`}
              placeholder="Enter the quote text..."
            />
            {errors.text && <p className="mt-1 text-sm text-red-500">{errors.text}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Author <span className="text-red-500">*</span>
              </label>
              <select
                value={form.authorId ?? ''}
                onChange={(e) => updateField('authorId', e.target.value ? Number(e.target.value) : null)}
                className={`w-full rounded-2xl border bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                  errors.authorId ? 'border-red-500' : 'border-surface-200'
                }`}
              >
                <option value="">Select author...</option>
                {authors?.map((author: any) => (
                  <option key={author.id} value={author.id}>
                    {author.name}
                  </option>
                ))}
              </select>
              {errors.authorId && <p className="mt-1 text-sm text-red-500">{errors.authorId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={form.categoryId ?? ''}
                onChange={(e) => updateField('categoryId', e.target.value ? Number(e.target.value) : null)}
                className={`w-full rounded-2xl border bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                  errors.categoryId ? 'border-red-500' : 'border-surface-200'
                }`}
              >
                <option value="">Select category...</option>
                {categories?.map((cat: any) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && <p className="mt-1 text-sm text-red-500">{errors.categoryId}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Language</label>
            <select
              value={form.language}
              onChange={(e) => updateField('language', e.target.value)}
              className="w-full rounded-2xl border border-surface-200 bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Tags</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); }
                }}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" variant="secondary" onClick={handleAddTag}>
                Add
              </Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-primary-50 text-primary-600"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-500 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card variant="glass" className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-surface-900">Options</h3>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-surface-700">Published</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(e) => updateField('isPublished', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-200 rounded-full peer peer-checked:bg-primary-500 transition-colors" />
              <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-surface-700">Featured</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(e) => updateField('isFeatured', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-200 rounded-full peer peer-checked:bg-primary-500 transition-colors" />
              <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
          </label>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Schedule Date</label>
            <Input
              type="datetime-local"
              value={form.scheduledAt}
              onChange={(e) => updateField('scheduledAt', e.target.value)}
            />
            <p className="mt-1 text-xs text-surface-400">Leave empty to publish immediately</p>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/quotes')}>
            Cancel
          </Button>
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {saveMutation.isPending ? 'Saving...' : 'Save Quote'}
          </Button>
        </div>
      </form>
    </div>
  );
}
